import { Link } from 'react-router-dom'

import { NICHT_VORHANDEN, api, ohne404 } from '../api'
import { datum, zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import { laufPfad } from '../pfade'
import { ENTSCHEIDUNGART } from '../texte'
import { blockName, wertText } from './Entscheidungskarte'
import Meldung from './Meldung'
import type { Entscheidung } from '../typen'

/**
 * Die gemerkten Entscheidungen des Excel-Imports (18.09.2026): was die Nutzerin einmal zu einem Blatt
 * entschieden hat (Art, Gruppe, Laden ja/nein, Kopplung eines Blocks), übernimmt das Backend beim
 * nächsten Lauf mit derselben Datei und fragt nicht erneut. „Vergessen" löscht den Eintrag - beim
 * nächsten Lauf wird wieder gefragt.
 */
export default function GemerkteEntscheidungen() {
  const { daten: geholt, fehler: ladeFehler, neuLaden } = useLaden<Entscheidung[] | typeof NICHT_VORHANDEN>(() => ohne404(api.entscheidungen()), [])
  const { fehler, arbeitet, ausfuehren } = useAktion()
  const fehlt = geholt === NICHT_VORHANDEN
  const daten = fehlt ? null : geholt

  function vergessen(e: Entscheidung) {
    if (!window.confirm(`Entscheidung „${ENTSCHEIDUNGART[e.art]}“ für Blatt „${e.blatt}“${e.block ? `, Block „${e.block}“` : ''} vergessen? Beim nächsten Lauf wird wieder gefragt.`)) return
    void ausfuehren(async () => {
      await api.entscheidungLoeschen(e.id)
      neuLaden()
    })
  }

  return (
    <div className="karte">
      <div className="leiste">
        <b>Gemerkte Entscheidungen</b>
        <span className="hinweis">Was beim Excel-Import einmal entschieden wurde, gilt beim nächsten Lauf derselben Datei ohne Nachfrage.</span>
      </div>
      {ladeFehler && <Meldung art="fehler">Die gemerkten Entscheidungen ließen sich nicht laden: {ladeFehler}</Meldung>}
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      {fehlt && <p className="hinweis">Diese Auswertung liefert das Backend noch nicht.</p>}
      {!fehlt && (
        <div className="tabellenrahmen">
          <table className="entscheidungen-tabelle">
            <thead>
              <tr>
                <th>Blatt</th>
                <th>Block</th>
                <th>Art</th>
                <th>Wert</th>
                <th>Seit</th>
                <th>Benutzt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {daten && daten.length === 0 && (
                <tr>
                  <td colSpan={7} className="leer">
                    Noch nichts gemerkt.
                  </td>
                </tr>
              )}
              {(daten ?? []).map((e) => (
                <tr key={e.id}>
                  <td>{e.blatt}</td>
                  <td title={e.block ?? undefined}>{e.block ? blockName(e.block) : ''}</td>
                  <td>{ENTSCHEIDUNGART[e.art] ?? e.art}</td>
                  <td>{wertText(e.wert)}</td>
                  <td title={`Quelle: ${e.quelle}`}>
                    {datum(e.angelegt_am.slice(0, 10))}
                    {e.lauf_id !== null && (
                      <>
                        {' '}
                        <Link className="hinweis-link" to={laufPfad({ id: e.lauf_id, dokumenttyp: 'excel' })}>
                          Lauf {e.lauf_id}
                        </Link>
                      </>
                    )}
                  </td>
                  <td title={e.zuletzt_benutzt_am ? `zuletzt ${zeitpunkt(e.zuletzt_benutzt_am)}` : 'noch nicht benutzt'}>
                    {e.benutzt === 0 ? 'noch nie' : `${e.benutzt} ×`}
                  </td>
                  <td>
                    <button className="leise" disabled={arbeitet} onClick={() => vergessen(e)}>
                      Vergessen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
